import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDateTime } from "@/components/badges";
import { Copy, File as FileIcon, FolderOpen, Link2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "@/components/ConfirmDialog";

export default function SharedLinks() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { askConfirm, confirmDialog } = useConfirm();
  const { data: links = [], isLoading } = useQuery({
    queryKey: ["drive-shares"],
    queryFn: async () => (await api.get("/drive/shares")).data,
  });

  const revokeMut = useMutation({
    mutationFn: async (id) => api.delete(`/drive/shares/${id}`),
    onSuccess: () => {
      toast.success(t("sharedLinks.revokedToast"));
      qc.invalidateQueries({ queryKey: ["drive-shares"] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const copy = (url) => navigator.clipboard.writeText(url).then(() => toast.success(t("sharedLinks.copiedToast"))).catch(() => toast.error(t("sharedLinks.copyFailed")));

  const statusBadge = (s) =>
    s.status === "active" ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700">{t("badges.active")}</Badge>
    : s.status === "expired" ? <Badge variant="outline" className="bg-slate-100 text-slate-500">{t("badges.expired")}</Badge>
    : <Badge variant="outline" className="bg-rose-50 text-rose-600">{t("badges.revoked")}</Badge>;

  return (
    <Card data-testid="shared-links">
      <CardContent className="overflow-x-auto p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("sharedLinks.thTarget")}</TableHead>
              {(user?.is_super_admin || user?.is_admin) && <TableHead>{t("sharedLinks.thCreatedBy")}</TableHead>}
              <TableHead>{t("sharedLinks.thCreated")}</TableHead>
              <TableHead>{t("sharedLinks.thExpires")}</TableHead>
              <TableHead>{t("sharedLinks.thPermission")}</TableHead>
              <TableHead>{t("common.status")}</TableHead>
              <TableHead className="text-right">{t("sharedLinks.thOpens")}</TableHead>
              <TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={8} className="text-center text-sm text-slate-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />{t("common.loading")}</TableCell></TableRow>}
            {!isLoading && links.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-sm text-slate-500" data-testid="shared-links-empty">{t("sharedLinks.empty")}</TableCell></TableRow>
            )}
            {links.map((l) => (
              <TableRow key={l.id} data-testid={`share-link-row-${l.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {l.target_type === "folder" ? <FolderOpen className="h-4 w-4 text-sky-600" /> : <FileIcon className="h-4 w-4 text-slate-400" />}
                    <span className="max-w-56 truncate font-medium text-slate-900" title={l.label}>{l.label}</span>
                  </div>
                </TableCell>
                {(user?.is_super_admin || user?.is_admin) && <TableCell className="text-xs text-slate-500">{l.created_by_name || l.created_by_email}</TableCell>}
                <TableCell className="text-xs text-slate-500">{fmtDateTime(l.created_at)}</TableCell>
                <TableCell className="text-xs text-slate-500">{new Date(l.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={l.permission === "view_download" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}>
                    {l.permission === "view_download" ? t("shareDialog.viewDownload") : t("shareDialog.viewOnly")}
                  </Badge>
                </TableCell>
                <TableCell>{statusBadge(l)}</TableCell>
                <TableCell className="text-right font-mono text-xs">{l.access_count || 0}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {l.status === "active" && (
                      <Button variant="ghost" size="icon" title={t("sharedLinks.copyLinkT")} onClick={() => copy(l.url)} data-testid={`share-link-copy-${l.id}`}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    )}
                    {l.status !== "revoked" && (
                      <Button variant="ghost" size="icon" title={t("sharedLinks.revokeT")} className="text-rose-600" onClick={() => askConfirm(t("sharedLinks.revokeConfirm", { label: l.label }), () => revokeMut.mutate(l.id), t("sharedLinks.revokeT"))} data-testid={`share-link-revoke-${l.id}`}>
                        <Link2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
      {confirmDialog}
    </Card>
  );
}
